import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import type { AccountInfo } from "@azure/msal-browser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { accessApi } from "../api/access";
import { configureApi } from "../api/httpConfig";
import type { CallerContext } from "../api/generated/contracts";
import { AuthContext } from "./AuthContext";
import type { AuthStatus } from "./AuthContext";
import { devBypassCaller, isDevBypassEnabled } from "./devBypass";
import { apiScopes, buildMsalConfig, isAuthConfigured } from "./msalConfig";

interface AuthProviderProps {
  readonly children: ReactNode;
}

/**
 * MSAL-backed auth: PKCE via @azure/msal-browser, config-driven authority/clientId/scopes
 * (auth/msalConfig.ts), role extraction from `GET /api/users/me` (never from a token claim -
 * CallerContextSource.cs explains why: role assignment lives in this application's own store,
 * D-153/D-166, not in a directory group). Wires http.ts's token provider and 401 handler once,
 * at mount, so every feature client under api/ authenticates the same way without importing MSAL.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const bypass = isDevBypassEnabled();
  const configured = isAuthConfigured();

  const msal = useMemo(() => (configured && !bypass ? new PublicClientApplication(buildMsalConfig()) : null), [configured, bypass]);
  const initialized = useRef(false);

  const [status, setStatus] = useState<AuthStatus>(bypass ? "authenticated" : "initializing");
  const [caller, setCaller] = useState<CallerContext | null>(bypass ? devBypassCaller("Admin") : null);

  const activeAccount = useCallback((): AccountInfo | null => {
    if (!msal) return null;
    return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  }, [msal]);

  const getAccessToken = async (): Promise<string | null> => {
    if (bypass) return "dev-bypass-token";
    if (!msal) return null;

    const account = activeAccount();
    if (!account) return null;

    try {
      const result = await msal.acquireTokenSilent({ scopes: apiScopes(), account });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await msal.acquireTokenRedirect({ scopes: apiScopes(), account });
      }
      return null;
    }
  };

  const login = (): void => {
    if (bypass) return;
    void msal?.loginRedirect({ scopes: apiScopes() });
  };

  const logout = (): void => {
    if (bypass) {
      setCaller(null);
      setStatus("unauthenticated");
      return;
    }
    const account = activeAccount();
    void msal?.logoutRedirect(account ? { account } : undefined);
  };

  useEffect(() => {
    configureApi({
      getAccessToken,
      onUnauthenticated: () => {
        setStatus("unauthenticated");
        setCaller(null);
      },
    });
    // getAccessToken/onUnauthenticated close over stable refs (msal, bypass); re-registering on
    // every render would be harmless but wasteful, so this effect intentionally runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bypass) return;
    if (!msal) {
      setStatus("unauthenticated");
      return;
    }
    if (initialized.current) return;
    initialized.current = true;

    let cancelled = false;

    void (async () => {
      await msal.initialize();
      const redirectResult = await msal.handleRedirectPromise();
      if (redirectResult?.account) {
        msal.setActiveAccount(redirectResult.account);
      }

      const account = activeAccount();
      if (!account) {
        if (!cancelled) setStatus("unauthenticated");
        return;
      }

      try {
        const resolved = await accessApi.getCurrentUser();
        if (!cancelled) {
          setCaller(resolved);
          setStatus("authenticated");
        }
      } catch {
        if (!cancelled) setStatus("unauthenticated");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [msal, bypass, activeAccount]);

  const hasRole = (...roles: readonly string[]): boolean => {
    if (!caller) return false;
    return roles.includes(caller.role);
  };

  const value = { status, caller, login, logout, hasRole };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
